import Honeybadger from '@honeybadger-io/js';
import type { ComponentType, ErrorInfo, ReactElement, ReactNode } from 'react';
import * as React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ErrorComponent } from 'common/components/ErrorComponent';
import StrictModeInDevelopment from 'common/components/StrictModeInDevelopment';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- this registry mounts arbitrary components with arbitrary prop shapes, so there's no narrower type to give props
type AnyProps = Record<string, any>;

type ComponentMap = Record<string, ComponentType<AnyProps>>;
type LazyComponentMap = Record<string, () => Promise<ComponentType<AnyProps>>>;

// window.Venuu has no global TS declaration (many boot entry-point files
// each merge their own components into it, still Flow) — cast locally at
// the point of use, same workaround reactMount.test.tsx already relies on.
type VenuuGlobal = {
  REACT_COMPONENTS?: ComponentMap;
  REACT_LAZY_COMPONENTS?: LazyComponentMap;
  REACT_PROPS: Record<string, AnyProps>;
};

function venuu(): VenuuGlobal {
  return (window as unknown as { Venuu: VenuuGlobal }).Venuu;
}

const roots: WeakMap<HTMLElement, Root> = new WeakMap();

function getRoot(node: HTMLElement, identifierPrefix: string): Root {
  const existing = roots.get(node);
  if (existing) return existing;

  // The prefix is the mount's component id, so every id generated inside a root
  // is traceable to the mount that owns it. These do not reproduce the ids the
  // prerender emitted for the same mount — React marks server-generated ids
  // apart from client-generated ones — and they don't need to, because this is
  // createRoot rather than hydrateRoot: the client render replaces the
  // prerendered DOM, ids included.
  const root = createRoot(node, { identifierPrefix });
  roots.set(node, root);
  return root;
}

export function unmountComponent(node: HTMLElement) {
  const root = roots.get(node);
  if (!root) return;

  root.unmount();
  roots.delete(node);
  delete node.dataset.reactMounted;
}

// Marks the mount container once React has committed — the earliest moment
// the component's form fields and handlers actually exist. render() returning
// is not that moment, and for lazy components the chunk fetch hasn't even
// started then. The marker lives on the container node because neither
// createRoot nor hydrateRoot ever touches the container's own attributes,
// and it sits outside the error boundary so a component that crashes into
// ErrorComponent still marks its container — pre-mount submit guards read
// this attribute, and an unmarked mount would freeze its form's submit for
// good. The effect re-running (StrictMode) is harmless: same attribute, and
// the event is only informational.
function MountedMarker({
  node,
  children
}: {
  node: HTMLElement;
  children: ReactNode;
}): ReactNode {
  React.useEffect(() => {
    node.dataset.reactMounted = 'true';
    node.dispatchEvent(
      new CustomEvent('venuu:react-mounted', { bubbles: true })
    );
  }, [node]);
  return children;
}

type MountErrorBoundaryProps = {
  componentName: string;
  children: ReactNode;
};

// Shows ErrorComponent in place of a mount that crashed and reports the crash
// under the mount's registered name. The name has to travel with the boundary:
// Honeybadger's context is global, and a root renders only after `render()`
// has returned, often in the same task as other roots, so a context set around
// one mount no longer describes it by the time its component throws. Props stay
// out of the report because they can carry personal data.
class MountErrorBoundary extends React.Component<
  MountErrorBoundaryProps,
  { crashed: boolean }
> {
  constructor(props: MountErrorBoundaryProps) {
    super(props);
    this.state = { crashed: false };
  }

  static getDerivedStateFromError(): { crashed: boolean } {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    Honeybadger.notify(error, {
      context: { ...info, component: this.props.componentName }
    });
  }

  render(): ReactNode {
    return this.state.crashed ? <ErrorComponent /> : this.props.children;
  }
}

export function exposeComponents(mountableComponents: ComponentMap) {
  venuu().REACT_COMPONENTS = {
    ...venuu().REACT_COMPONENTS,
    ...mountableComponents
  };
}

export function exposeLazyComponents(mountableComponents: LazyComponentMap) {
  venuu().REACT_LAZY_COMPONENTS = {
    ...venuu().REACT_LAZY_COMPONENTS,
    ...mountableComponents
  };
}

export function createElement(
  component: ComponentType<AnyProps>,
  props: AnyProps
): ReactNode {
  return React.createElement(component, props);
}

const mountComponentFromNode = (node: HTMLElement) => {
  const componentName = node.getAttribute('data-react-class');
  const componentId = node.getAttribute('data-component-id');
  if (!componentName || !componentId) {
    throw new Error("Can't mount react node");
  }
  const props = venuu().REACT_PROPS[`${componentName}:${componentId}`];
  mountComponent(componentName, props, node, componentId);
};

function mountComponent(
  componentName: string,
  props: AnyProps,
  node: HTMLElement,
  componentId: string
) {
  const normalComponent =
    venuu().REACT_COMPONENTS && venuu().REACT_COMPONENTS?.[componentName];
  const loadLazyComponent =
    venuu().REACT_LAZY_COMPONENTS &&
    venuu().REACT_LAZY_COMPONENTS?.[componentName];

  if (normalComponent) {
    render(componentName, normalComponent, props, node, componentId);
  } else if (loadLazyComponent) {
    loadLazyComponent()
      .then(component => {
        render(componentName, component, props, node, componentId);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  } else {
    throw new Error(
      `Could not find mountable component ${componentName}. ` +
        'You need to expose it through REACT_COMPONENTS or REACT_LAZY_COMPONENTS.'
    );
  }
}

function render(
  componentName: string,
  component: ComponentType<AnyProps>,
  props: AnyProps,
  node: HTMLElement,
  identifierPrefix: string
) {
  const element = createElement(component, props) as ReactElement;
  // TODO: Call hydrateRoot instead when the component has been server-rendered
  getRoot(node, identifierPrefix).render(
    <StrictModeInDevelopment>
      <MountedMarker node={node}>
        <MountErrorBoundary componentName={componentName}>
          {element}
        </MountErrorBoundary>
      </MountedMarker>
    </StrictModeInDevelopment>
  );
}

export function mountAllComponents() {
  const reactNodes =
    document.querySelectorAll<HTMLElement>('[data-react-class]');

  // YYyeah, document.querySelectorAll doesn't return an Array but a NodeList object. And that
  // fucker naturally doesn't support .map function, because DOM API sucks balls, so we have to
  // rely on normal for-loop to iterate the nodes.
  // https://developer.mozilla.org/en-US/docs/Web/API/NodeList#Why_is_NodeList_not_an_Array
  for (let i = 0; i < reactNodes.length; i++) {
    mountComponentFromNode(reactNodes[i]);
  }
}
